import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { StudentAccessResult } from '../services/classCodeService';
import AccessCodeChangeModal from './AccessCodeChangeModal';
import ProfileImageSelectModal from './ProfileImageSelectModal';
import CachedProfileImage from './CachedProfileImage';

interface StudentProfileEditModalProps {
  isOpen: boolean;
  resultData: StudentAccessResult | null;
  onClose: () => void;
  onAccessCodeUpdated?: (newAccessCode: string) => void;
  onProfileImageUpdated?: (imageId: string) => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const ModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  z-index: 10000;
  border: var(--modal-border);
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
  font-size: 1.3rem;
  text-align: center;
`;

const ProfileImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`;

const ProfileImageWrapper = styled.div`
  width: 120px;
  height: 120px;
  border: 2px solid #000000;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ProfileInfo = styled.div`
  margin-bottom: 12px;
  text-align: center;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px 0;
  font-size: 1.2rem;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: var(--color-text-gray);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const InfoValue = styled.span`
  color: var(--color-text-gray);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 16px 0;
`;

const AccessCodeChangeButton = styled.button`
  background: var(--color-panel);
  color: var(--color-text-gray);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  
  &:hover {
    background: var(--color-border);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const StudentProfileEditModal: React.FC<StudentProfileEditModalProps> = ({
  isOpen,
  resultData,
  onClose,
  onAccessCodeUpdated,
  onProfileImageUpdated
}) => {
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [showProfileImageModal, setShowProfileImageModal] = useState(false);
  const [currentProfileImageId, setCurrentProfileImageId] = useState<string>('basic-0');

  // 스크롤 방지 및 프로필 이미지 초기화
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // 모달이 열릴 때 현재 프로필 이미지 ID 초기화
      const student = resultData?.student;
      if (student) {
        const initialImageId = student.profileImage || student.basicInfo?.profileImage || 'basic-0';
        setCurrentProfileImageId(initialImageId);
      }
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, resultData]);

  if (!isOpen) return null;

  const student = resultData?.student;
  const isGuestMode = resultData?.isGuestMode || false;

  if (isGuestMode) {
    return null; // 게스트 모드에서는 표시하지 않음
  }

  if (!student) {
    return null;
  }

  const handleAccessCodeChange = () => {
    setShowAccessCodeModal(true);
  };

  const handleAccessCodeChangeClose = () => {
    setShowAccessCodeModal(false);
  };

  const handleProfileImageClick = () => {
    setShowProfileImageModal(true);
  };

  const handleProfileImageConfirm = (imageId: string) => {
    setShowProfileImageModal(false);
    // 프로필 이미지 즉시 업데이트
    setCurrentProfileImageId(imageId);
    if (onProfileImageUpdated) {
      onProfileImageUpdated(imageId);
    }
  };

  const effectiveProfileImageId = currentProfileImageId;
  const unlockedProfiles = student?.basicInfo?.unlockedProfiles || [];

  return (
    <>
      <ModalOverlay isOpen={isOpen} onClick={onClose} />
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>프로필 편집</Title>
        
        <ProfileImageContainer>
          <ProfileImageWrapper onClick={handleProfileImageClick}>
            <CachedProfileImage
              imageId={effectiveProfileImageId}
              alt="프로필"
              fallbackImageId="basic-0"
            />
          </ProfileImageWrapper>
        </ProfileImageContainer>
        
        <ProfileInfo>
          <InfoRow>
            <InfoLabel>번호 :</InfoLabel>
            <InfoValue>{student.basicInfo.number || '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>이름 :</InfoLabel>
            <InfoValue>{student.basicInfo.name || '-'}</InfoValue>
          </InfoRow>
        </ProfileInfo>

        <Divider />

        <AccessCodeChangeButton onClick={handleAccessCodeChange}>
          입장코드 변경
        </AccessCodeChangeButton>
      </ModalContent>

      {showAccessCodeModal && (
        <AccessCodeChangeModal
          isOpen={showAccessCodeModal}
          resultData={resultData}
          onClose={handleAccessCodeChangeClose}
          onSuccess={(newAccessCode) => {
            handleAccessCodeChangeClose();
            if (onAccessCodeUpdated) {
              onAccessCodeUpdated(newAccessCode);
            }
          }}
        />
      )}

      {showProfileImageModal && resultData?.student && (
        <ProfileImageSelectModal
          isOpen={showProfileImageModal}
          onClose={() => setShowProfileImageModal(false)}
          onConfirm={handleProfileImageConfirm}
          currentImageId={effectiveProfileImageId}
          unlockedProfiles={unlockedProfiles}
        />
      )}
    </>
  );
};

export default StudentProfileEditModal;

